import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import DirectionsCarIcon from '@mui/icons-material/DirectionsCarOutlined'
import FactoryIcon from '@mui/icons-material/FactoryOutlined'
import WarehouseIcon from '@mui/icons-material/WarehouseOutlined'
import { useTranslation } from 'next-i18next'
import { LocationType, SizeFilter } from 'src/types/graphql'
import { FilterOption, FiltersContentProps, SpaceType } from './types'

export const SIZE_TYPE_MAP: { [key in SizeFilter]: SpaceType } = {
  [SizeFilter.Any]: SpaceType.ALL,
  [SizeFilter.Parking]: SpaceType.PARKING,
  [SizeFilter.Xsmall]: SpaceType.SELF_STORAGE,
  [SizeFilter.Small]: SpaceType.SELF_STORAGE,
  [SizeFilter.Medium]: SpaceType.SELF_STORAGE,
  [SizeFilter.Large]: SpaceType.SELF_STORAGE,
  [SizeFilter.Commercial]: SpaceType.WAREHOUSE,
  [SizeFilter.Industrial]: SpaceType.INDUSTRIAL_WAREHOUSE,
  [SizeFilter.Nave]: SpaceType.INDUSTRIAL_WAREHOUSE
}

// Each user-facing SpaceType maps to the granular `LocationType` enum values
// the backend stores on configurations. Without this, the search query falls
// back to `sizes` + `canStoreVehicle` which CAN'T disambiguate when:
//   1. Mixed canStoreVehicle picks (e.g. PARKING + INDUSTRIAL_WAREHOUSE)
//      collapse to `undefined`, removing the only vehicle/non-vehicle
//      discriminator.
//   2. The numeric `sizes` filter is location-type-agnostic — a 6 m²
//      mini-bodega matches PARKING's size range, etc.
// Grouping mirrors the backend's `SIMPLIFIED_LOCATION_TYPE`
// (spotme-server/src/constants/index.ts) plus the `BodegaCommercial` /
// `NaveIndustrial` distinction we expose separately in the UI.
export const SPACE_TYPE_LOCATION_TYPES: Partial<
  Record<SpaceType, LocationType[]>
> = {
  [SpaceType.SELF_STORAGE]: [
    LocationType.SelfStorageUnit,
    LocationType.Room,
    LocationType.Local,
    LocationType.Yard,
    LocationType.Terrace,
    LocationType.Shed,
    LocationType.ShippingContainer,
    LocationType.Closet,
    LocationType.Basement,
    LocationType.Attic,
    LocationType.Other,
    LocationType.House,
    LocationType.MiniWinery,
    LocationType.Penthouse
  ],
  [SpaceType.PARKING]: [LocationType.Parking, LocationType.Garage],
  [SpaceType.WAREHOUSE]: [
    LocationType.Warehouse,
    LocationType.BodegaCommercial,
    LocationType.Office
  ],
  [SpaceType.INDUSTRIAL_WAREHOUSE]: [
    LocationType.NaveIndustrial,
    LocationType.BodegaIndustrial
  ],
  [SpaceType.LAND]: [LocationType.Land]
}

export const SPACE_TYPE_MAP: { [key in SpaceType]: FilterOption } = {
  [SpaceType.SELF_STORAGE]: {
    id: SpaceType.SELF_STORAGE,
    label: 'SEARCH_BAR.SELF_STORAGE',
    icon: 'M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4',
    query: {
      canStoreVehicle: false,
      sizes: [
        SizeFilter.Xsmall,
        SizeFilter.Small,
        SizeFilter.Medium,
        SizeFilter.Large
      ]
    }
  },
  [SpaceType.PARKING]: {
    id: SpaceType.PARKING,
    label: 'SEARCH_BAR.PARKING',
    muiIcon: DirectionsCarIcon,
    query: {
      canStoreVehicle: true,
      sizes: [SizeFilter.Parking]
    }
  },
  [SpaceType.WAREHOUSE]: {
    id: SpaceType.WAREHOUSE,
    label: 'SEARCH_BAR.WAREHOUSE',
    muiIcon: WarehouseIcon,
    query: {
      canStoreVehicle: false,
      sizes: [SizeFilter.Commercial, SizeFilter.Industrial]
    }
  },
  [SpaceType.INDUSTRIAL_WAREHOUSE]: {
    id: SpaceType.INDUSTRIAL_WAREHOUSE,
    label: 'SEARCH_BAR.INDUSTRIAL',
    muiIcon: FactoryIcon,
    query: {
      canStoreVehicle: false,
      sizes: [SizeFilter.Nave]
    }
  },
  [SpaceType.LAND]: {
    id: SpaceType.LAND,
    label: 'SEARCH_BAR.LAND',
    muiIcon: CheckBoxOutlineBlankIcon,
    query: {
      locationTypes: [LocationType.Land]
    }
  },
  [SpaceType.ALL]: {
    id: SpaceType.ALL,
    label: 'SEARCH_BAR.ALL_TYPES',
    icon: 'M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10',
    query: {
      // No specific filters - searches for everything
    }
  }
}

export const getEffectiveSpaceTypes = (filters: {
  spaceTypes?: SpaceType[]
  sizes?: SizeFilter[]
}): SpaceType[] | undefined => {
  if (filters.spaceTypes && filters.spaceTypes.length > 0) {
    return filters.spaceTypes
  }
  if (filters.sizes && filters.sizes.length > 0) {
    return [...new Set(filters.sizes.map((size) => SIZE_TYPE_MAP[size]))]
  }
  return undefined
}

// Shared helpers for deriving backend-query filters from a `sizes` selection.
// Used by every filter UI that lets the user pick sizes directly (Filters
// drawer, QuickFilters chips). Keeping them here means all three filter
// pathways (search bar, drawer, quick-filter chips) stay in sync.
export function deriveCanStoreVehicle(
  sizes: SizeFilter[] | undefined
): boolean | undefined {
  if (!sizes || sizes.length === 0) return undefined
  const spaceTypes = [...new Set(sizes.map((size) => SIZE_TYPE_MAP[size]))]
  const values = spaceTypes.map(
    (type) => SPACE_TYPE_MAP[type].query.canStoreVehicle
  )
  const mixed =
    (values.some((v) => v === true) && values.some((v) => v === false)) ||
    values.every((v) => v === undefined)
  if (mixed) return undefined
  return values.some((v) => v === true) || false
}

export function deriveLocationTypes(
  sizes: SizeFilter[] | undefined
): LocationType[] | undefined {
  if (!sizes || sizes.length === 0) return undefined
  const spaceTypes = [...new Set(sizes.map((size) => SIZE_TYPE_MAP[size]))]
  const locationTypes = [
    ...new Set(
      spaceTypes.flatMap((type) => SPACE_TYPE_LOCATION_TYPES[type] ?? [])
    )
  ]
  return locationTypes.length > 0 ? locationTypes : undefined
}

export const getSpaceTypeOptions = (): FilterOption[] => {
  return Object.values(SPACE_TYPE_MAP)
}

export const getSearchBarIcons = (
  selectedFilters: SpaceType[]
): FilterOption[] => {
  if (selectedFilters.length === 0) {
    return [
      SPACE_TYPE_MAP[SpaceType.SELF_STORAGE],
      SPACE_TYPE_MAP[SpaceType.PARKING]
    ]
  }

  const icons = selectedFilters
    .slice(0, 2)
    .map((filterId) => SPACE_TYPE_MAP[filterId])
    .filter(Boolean) as FilterOption[]

  return icons
}

const SpaceTypeFilters = ({
  title = 'SEARCH_BAR.WHAT_STORING_PARKING',
  selectedFilters = [],
  onFilterSelect,
  onReset,
  onShowSpaces
}: FiltersContentProps) => {
  const { t } = useTranslation('common')
  const spaceTypeOptions = getSpaceTypeOptions()

  const handleSpaceTypeClick = (filterId: SpaceType) => {
    onFilterSelect?.(filterId)
  }

  const isSpaceTypeSelected = (filterId: SpaceType): boolean => {
    return selectedFilters.includes(filterId)
  }

  const allIsSelected = selectedFilters.includes(SpaceType.ALL)

  return (
    <div className="overflow-hidden md:p-6">
      <h3 className="mb-4 text-base font-semibold text-ink-800">{t(title)}</h3>

      {/* Filter Options */}
      <div className="grid grid-cols-2 gap-2.5 overflow-hidden md:mb-6 md:grid-cols-3">
        {spaceTypeOptions.map((option) => {
          const IconComponent = option.muiIcon
          const isSelected = isSpaceTypeSelected(option.id)

          return (
            <button
              key={option.id}
              type="button"
              aria-pressed={isSelected}
              onClick={() => handleSpaceTypeClick(option.id)}
              className={`overflow-hidden rounded-xl border p-3 text-xs font-medium transition-colors duration-150 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 sm:p-3.5 sm:text-sm ${
                isSelected
                  ? 'border-blue-800/40 bg-blue-500/10 text-blue-800 hover:bg-blue-500/15'
                  : allIsSelected && option.id !== SpaceType.ALL
                    ? 'border-blue-800/20 bg-cream-300/40 text-ink-700 hover:bg-cream-300/70'
                    : 'border-ink-100/20 bg-white-500 text-ink-700 hover:border-ink-100/40 hover:bg-cream-300/60'
              }`}
            >
              <div className="flex items-center justify-center space-x-1.5 sm:space-x-2">
                {IconComponent && (
                  <IconComponent
                    className="size-3.5 sm:size-4"
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 300,
                      strokeWidth: 0.5
                    }}
                  />
                )}
                {option.icon && (
                  <svg
                    className="size-3.5 sm:size-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d={option.icon}
                    />
                  </svg>
                )}
              </div>
              <span className="mt-1 block text-center leading-tight">
                {t(option.label)}
              </span>
            </button>
          )
        })}
      </div>

      {/* Action Buttons */}
      <div className="hidden items-center justify-between md:flex">
        <button
          type="button"
          onClick={onReset}
          className="rounded text-sm font-medium text-ink-700 underline-offset-4 transition-colors hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800"
        >
          {t('SEARCH_BAR.RESET')}
        </button>
        <button
          type="button"
          onClick={onShowSpaces}
          className="rounded-full bg-blue-500 px-6 py-2.5 text-sm font-semibold text-white-500 shadow-[0_8px_20px_-8px_rgba(28,100,242,0.55)] transition-[background-color,box-shadow,transform] duration-200 hover:bg-blue-600 hover:shadow-[0_10px_24px_-8px_rgba(28,100,242,0.65)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-800 active:scale-[0.98]"
        >
          {t('SEARCH_BAR.SHOW_SPACES')}
        </button>
      </div>
    </div>
  )
}

export default SpaceTypeFilters
